import {Router} from '@angular/router';
import {ComponentType} from '@angular/cdk/portal';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';

import {map} from 'rxjs/operators';
import {forkJoin, Observable, Subscription} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    TradingAccountStatusesEnum,
    TradingService,
    PermissionsService,
    PermissionInterface,
    TradingClientInterface,
    TradingAccountAccessInterface,
    OwnerTradingAccountInterface,
    PermissionModel,
    TradingGroupModel,
    CompanyExtendedModel,
    LinkedAccountModel,
    TradingClientModel,
    TradingAccountModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    OwnerTradingAccountModel,
    TradingClientSearchModel,
    TradingAccountAccessModel,
} from '@spa/api';

import {Helper} from '../../../../shared/classes';
import {PanelPaths} from '../../../../shared/paths';
import {AlertTypesEnum} from '../../../../shared/enums';
import {AlertsService} from '../../../../shared/services';

import {EventsService} from '../../services';
import {PortalPermissionsTrait} from '../portal-permissions.trait';
import {AssignAccountsRightsResponseType} from '../../types';
import {TradingAccountsReducedObjectInterface} from '../../interfaces';
import {PanelClientsEditQuickActionsEventsEnum, PanelClientsEditResolverErrorStatesEnum} from '../../enums';

import {PanelClientsEditMessages} from '../../../../../assets/messages';

import {CompaniesPathSelector, IndividualPathSelector} from '../../../../../assets/descriptors/panel-clients';

export class PanelClientsEditTrait {
    private constructor() {
    }

    static quickActionsSuccessAfterClosedHandler(
        client: IndividualExtendedModel | CompanyExtendedModel,
        path: string,
        event: number,
        eventsService: EventsService,
        data: TradingClientModel | IndividualDocumentModel | LinkedAccountModel | boolean,
        router: Router
    ): void {
        const currentUrl = router.url;

        const regex = new RegExp('^.*' + path + '$');

        if (!regex.test(currentUrl)) {
            if (event !== PanelClientsEditQuickActionsEventsEnum.ChangeStatus) {
                let mainPath = IndividualPathSelector;

                if (client instanceof CompanyExtendedModel) {
                    mainPath = CompaniesPathSelector;
                }

                const clientsPath = PanelPaths.children.clients.path;
                const clientsPathRegex = new RegExp('^.*' + clientsPath + '.*$');

                router.navigate([
                    PanelPaths.root + '/' +
                    (!clientsPathRegex.test(currentUrl) ? PanelPaths.children.verifications.path : clientsPath) + '/' +
                    PanelPaths.children.clients.children.edit.path + '/' + client.id + '/' + mainPath + '/' + path
                ]).finally();
            } else {
                eventsService.forceIndividualTradingClientGet.next(data as TradingClientModel);
                eventsService.forceClientUserGet.next(true);
            }
        } else {
            switch (event) {
                case PanelClientsEditQuickActionsEventsEnum.ChangeStatus:
                    window.location.reload();
                    break;
                case PanelClientsEditQuickActionsEventsEnum.AddDocument:
                    eventsService.forceClientDocumentGet.next(data as IndividualDocumentModel);
                    break;
                case PanelClientsEditQuickActionsEventsEnum.AddTradingAccount:
                case PanelClientsEditQuickActionsEventsEnum.TradingAccountsBlocked:
                    eventsService.forceIndividualTradingClientGet.next(data as TradingClientModel);
                    break;
                case PanelClientsEditQuickActionsEventsEnum.MakeTransfer:
                    eventsService.forceIndividualTransactionsGet.next(data as boolean);
                    break;
                case PanelClientsEditQuickActionsEventsEnum.AddBank:
                    eventsService.forceIndividualBankAccountsGet.next(data as LinkedAccountModel);
                    break;
                case PanelClientsEditQuickActionsEventsEnum.AddUser:
                    eventsService.forceClientUserGet.next(true);

            }
        }
    }

    static searchTradingClient(
        clientId: number,
        isCorporateClient,
        tradingClient: TradingClientModel,
        tradingService: TradingService,
        alertsService: AlertsService,
        checkForNullData: boolean,
        cb: (client: TradingClientModel) => void
    ): Subscription | null {
        const searchModel = !isCorporateClient ?
            new TradingClientSearchModel(null, null, null, clientId, null, null) :
            new TradingClientSearchModel(null, null, null, null, clientId, null);

        if (!tradingClient) {
            return tradingService.searchClient(searchModel)
                .subscribe((response: ApiResponse<ApiError[] | TradingClientInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        alertsService.addAlert(
                            PanelClientsEditMessages.SearchTradingClientFailed.message,
                            AlertTypesEnum.Error
                        );

                        return false;
                    }

                    if (checkForNullData && (response.data === null)) {
                        alertsService.addAlert(
                            PanelClientsEditMessages.SearchTradingClientNotFound.message,
                            AlertTypesEnum.Error
                        );

                        return false;
                    }

                    let model = null;

                    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                        const data = response.data[0] as TradingClientInterface;

                        model = new TradingClientModel(data);
                    }

                    cb(model);
                });
        }

        cb(tradingClient);

        return null;
    }

    static getActiveReducedTradingAccounts(client: TradingClientModel): TradingAccountsReducedObjectInterface[] {
        return client.groups
            .reduce((array: TradingAccountsReducedObjectInterface[], group: TradingGroupModel) => {
                group.accounts.filter((account: TradingAccountModel) => {
                    return (Helper.enumsHelper().getActiveTradingAccountStatuses().indexOf(account.status) >= 0);
                }).forEach((account: TradingAccountModel) => {
                    if (!account.isCashAccount) {
                        const filteredCashAccounts = array.filter((arrayItem: TradingAccountsReducedObjectInterface) => {
                            return arrayItem.id === account.relatedCashAccountId;
                        });

                        if (filteredCashAccounts.length !== 0) {
                            filteredCashAccounts[0].accounts.push(account);
                        } else {
                            const cashAccounts = group.accounts.filter((cashAccount: TradingAccountModel) => {
                                return cashAccount.id === account.relatedCashAccountId;
                            });

                            array.push({
                                id: account.relatedCashAccountId,
                                reference: (cashAccounts.length > 0) ? cashAccounts[0].accountReferenceId : null,
                                status: null,
                                ownerName: null,
                                accounts: [account]
                            });
                        }
                    } else {
                        const filteredCashAccounts = array.filter((arrayItem: TradingAccountsReducedObjectInterface) => {
                            return arrayItem.id === account.id;
                        });

                        if (filteredCashAccounts.length !== 0) {
                            filteredCashAccounts[0].reference = account.accountReferenceId;
                            filteredCashAccounts[0].status = account.status;
                        } else {
                            array.push({
                                id: account.id,
                                reference: account.accountReferenceId,
                                status: account.status,
                                accounts: []
                            });
                        }
                    }

                    return array;
                });

                return array;
            }, []);
    }

    static blockTradingAccounts(
        tradingClient: TradingClientModel,
        data: number[]
    ): TradingClientModel {
        data.forEach((accountId: number) => {
            const account = tradingClient.getGroupAccountById(accountId);

            if (account) {
                account.status = TradingAccountStatusesEnum.Blocked;
            }
        });

        return tradingClient;
    }

    static navigateToCashAccountCreation(clientType: number, clientId: number, router: Router): void {
        router.navigate([
            PanelPaths.root,
            PanelPaths.children.accounts.path,
            PanelPaths.children.accounts.children.add.path,
            clientType,
            clientId,
            PanelPaths.children.accounts.children.add.children.cashAccount.path
        ]).finally();
    }

    static navigateToSelectCashAccount(clientType: number, clientId: number, router: Router): void {
        router.navigate([
            PanelPaths.root,
            PanelPaths.children.accounts.path,
            PanelPaths.children.accounts.children.use.path,
            clientType,
            clientId,
        ]).finally();
    }

    static getInitialValidationErrors(errors: ApiError[]): string[] {
        return errors.map((error: ApiError) => {
            switch (error.status) {
                case PanelClientsEditResolverErrorStatesEnum.GetExtendedIndividualFailed:
                    return PanelClientsEditMessages.GetExtendedIndividualFailed.message;
                case PanelClientsEditResolverErrorStatesEnum.GetExtendedIndividualNotFound:
                    return PanelClientsEditMessages.GetExtendedIndividualNotFound.message;
                case PanelClientsEditResolverErrorStatesEnum.GetCountriesFailed:
                    return PanelClientsEditMessages.GetCountriesFailed.message;
                case PanelClientsEditResolverErrorStatesEnum.GetCountriesInvalidData:
                    return PanelClientsEditMessages.GetCountriesInvalidData.message;
                default:
                    return PanelClientsEditMessages.UnknownError.message;
            }
        });
    }

    static continueClientRegistration(router, isCorporate: boolean, userId: number, clientId: number): void {
        if (!isCorporate) {
            router.navigate([
                PanelPaths.root,
                PanelPaths.children.clients.path,
                PanelPaths.children.clients.children.add.path,
                userId,
                PanelPaths.children.clients.children.add.children.individual.path,
                clientId
            ]).finally();
        } else {
            router.navigate([
                PanelPaths.root,
                PanelPaths.children.clients.path,
                PanelPaths.children.clients.children.add.path,
                userId,
                PanelPaths.children.clients.children.add.children.corporate.path,
                clientId
            ]).finally();
        }
    }

    static openDialog(dialog: MatDialog, component: ComponentType<any>, data: any): MatDialogRef<any, any> {
        return dialog.open(
            component, {
                width: '500px',
                data,
                disableClose: true
            });
    }

    static getAssignAccountRightsData(
        individualId: number,
        tradingService: TradingService,
        permissionsService: PermissionsService,
        alertsService: AlertsService
    ): Observable<{ accounts: OwnerTradingAccountModel[], permissions: PermissionModel[] }> {
        return forkJoin([
            tradingService.getOwnerTradingAccounts(individualId, UserRelationTypesEnum.Individual),
            tradingService.getAccountsAccessRights(individualId, UserRelationTypesEnum.Individual),
            PortalPermissionsTrait.getAllPortalPermissionsRequest(permissionsService)
        ]).pipe(
            map((responses: AssignAccountsRightsResponseType[]) => {
                const accountsResponse = responses[0] as ServerResponseData<OwnerTradingAccountInterface[]>;
                const accessRightsResponse = responses[1] as ServerResponseData<TradingAccountAccessInterface[]>;
                const permissionsResponse = responses[2] as ServerResponseData<PermissionInterface[]>;

                const errorMessages = [];

                if (accountsResponse.status !== HttpStatusCodesEnum.Ok) {
                    errorMessages.push(PanelClientsEditMessages.GetIndividualAccountsFailed.message);
                }

                if (Helper.errorsHelper().checkForInvalidRespondedArray<OwnerTradingAccountInterface>(accountsResponse)) {
                    errorMessages.push(PanelClientsEditMessages.GetIndividualAccountsNoData.message);
                }

                if (accessRightsResponse.status !== HttpStatusCodesEnum.Ok) {
                    errorMessages.push(PanelClientsEditMessages.GetIndividualAccountsRightsFailed.message);
                }

                if (permissionsResponse.status !== HttpStatusCodesEnum.Ok) {
                    errorMessages.push(PanelClientsEditMessages.GetPermissionsFailed.message);
                }

                if (Helper.errorsHelper().checkForInvalidRespondedArray<PermissionInterface>(permissionsResponse)) {
                    errorMessages.push(PanelClientsEditMessages.GetPermissionsNoData.message);
                }

                if (errorMessages.length > 0) {
                    errorMessages.map((message: string) => alertsService.addAlert(message));

                    return null;
                }

                let alreadySetupAccountIds = [];

                if (!Helper.errorsHelper().checkForInvalidRespondedArray<TradingAccountAccessInterface>(accessRightsResponse)) {
                    alreadySetupAccountIds = (accessRightsResponse.data as TradingAccountAccessInterface[])
                        .map((item: TradingAccountAccessInterface) => (new TradingAccountAccessModel(item)).accountId);
                }

                const accounts = (accountsResponse.data as OwnerTradingAccountInterface[])
                    .map((item: OwnerTradingAccountInterface) => new OwnerTradingAccountModel(item))
                    .filter((item: OwnerTradingAccountModel) => alreadySetupAccountIds.indexOf(item.id) < 0);

                if (accounts.length === 0) {
                    alertsService.addAlert(
                        PanelClientsEditMessages.NoAccountsForSetup.message,
                        AlertTypesEnum.Warning
                    );

                    return null;
                }

                const permissions = (permissionsResponse.data as PermissionInterface[]).map((item: PermissionInterface) => {
                    return new PermissionModel(item);
                });

                return {accounts, permissions};
            })
        );
    }
}
